import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription } from '../../../../components/ui/alert.js';
import { Button } from '../../../../components/ui/button.js';
import { Checkbox } from '../../../../components/ui/checkbox.js';
import { Combobox } from '../../../../components/ui/combobox.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.js';
import { cloneCustomForm, copyCustomForm, getMatchedFormResults } from '../actions.js';
import {
  resolveActionError,
  useActionLog,
  useCustomForms,
  useCustomFormDetails,
  useCustomFormsApi,
  useFilteredWorkspaceOptions,
} from '../hooks.js';
import type { WorkfrontCustomFormGroup, WorkfrontWorkspaceOption } from '../types.js';
import { CustomFormDetails } from './CustomFormDetails.js';
import { ParameterRow } from './ParameterRow.js';

interface CustomFormProps {
  workspaceOptions: ReadonlyArray<WorkfrontWorkspaceOption>;
  workspaceError: string | null;
  onCancel: () => void;
}

/**
 * Replace-Custom-Form wizard. Port of
 * `wf-audit/src/slices/workfront/components/custom-forms-migration/
 * custom-form/CustomForm.tsx` adapted for the new SPA's UI primitives
 * (shadcn `Select` + `Combobox` + `Dialog`).
 *
 * Flow:
 *  1. Pick source workspace + form (left panel — readable preview)
 *  2. Pick target workspace + form (right panel — the EXISTING form
 *     to overwrite, also previewable)
 *  3. Click "Review Changes" → calls `getMatchedFormResults` and
 *     renders a read-only diff (groups + params, with mismatch
 *     warnings on per-field config / option-set drift)
 *  4. Optional "Create backup" checkbox (default on) — clones the
 *     target to `<name>_bkup_MMDDYYYY-HHMM` before replacing
 *  5. Click "Replace" → confirm dialog → `copyCustomForm` with
 *     `formId: <target>` (triggers the engine handler's PUT variant
 *     against `attask/api/v21.0/CTGY/{id}`)
 *
 * The Replace flow shares all underlying actions with the Clone flow
 * (`cloneCustomForm` runs the backup; `copyCustomForm` runs the
 * PUT). The only Replace-specific UI is the dual-panel review + the
 * confirm dialog.
 */
export function CustomForm({
  workspaceOptions,
  workspaceError,
  onCancel,
}: CustomFormProps): JSX.Element {
  const api = useCustomFormsApi();
  const [leftWorkspaceRef, setLeftWorkspaceRef] = useState('');
  const [leftFormId, setLeftFormId] = useState<string | null>(null);
  const [rightWorkspaceRef, setRightWorkspaceRef] = useState('');
  const [rightFormId, setRightFormId] = useState<string | null>(null);

  const [createBackup, setCreateBackup] = useState(true);
  const [showLeftPreview, setShowLeftPreview] = useState(false);
  const [showRightPreview, setShowRightPreview] = useState(false);

  const [matchedResults, setMatchedResults] = useState<WorkfrontCustomFormGroup[] | null>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [compareError, setCompareError] = useState('');

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [replaceError, setReplaceError] = useState('');
  const [replaceSuccess, setReplaceSuccess] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const { logs, addLog, clearLogs } = useActionLog();

  const leftForms = useCustomForms(leftWorkspaceRef);
  const rightForms = useCustomForms(rightWorkspaceRef);
  const leftForm = useCustomFormDetails({ workspaceRef: leftWorkspaceRef, formId: leftFormId });
  const rightForm = useCustomFormDetails({ workspaceRef: rightWorkspaceRef, formId: rightFormId });

  const sourceOptions = useFilteredWorkspaceOptions(workspaceOptions, rightWorkspaceRef);
  const targetOptions = useFilteredWorkspaceOptions(workspaceOptions, leftWorkspaceRef);

  const clearComparison = (): void => {
    setMatchedResults(null);
    setCompareError('');
    setReplaceError('');
    setReplaceSuccess(false);
    clearLogs();
  };

  // Re-clear the comparison whenever either panel's selection changes —
  // the matched-results snapshot is no longer current.
  useEffect(() => {
    clearComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftWorkspaceRef, leftFormId, rightWorkspaceRef, rightFormId]);

  // Reset form selection when its workspace changes.
  useEffect(() => {
    setLeftFormId(null);
  }, [leftWorkspaceRef]);
  useEffect(() => {
    setRightFormId(null);
  }, [rightWorkspaceRef]);

  const isDisabled = leftForm.isLoading || rightForm.isLoading || isReplacing;

  const handleReviewChanges = async (): Promise<void> => {
    if (!leftForm.formDetails || !rightWorkspaceRef) return;
    setIsComparing(true);
    setCompareError('');
    setMatchedResults(null);

    try {
      const results = await getMatchedFormResults(api, leftForm.formDetails, {
        workspaceRef: rightWorkspaceRef,
      });
      setMatchedResults(results);
    } catch (error) {
      setCompareError(resolveActionError(error, 'Failed to compare forms'));
    } finally {
      setIsComparing(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const handleReplace = async (): Promise<void> => {
    if (!leftForm.formDetails || !matchedResults || !rightWorkspaceRef || !rightFormId) {
      return;
    }
    const destWorkspace = { workspaceRef: rightWorkspaceRef };

    setIsReplacing(true);
    setReplaceError('');
    setReplaceSuccess(false);
    clearLogs();

    try {
      if (createBackup && rightForm.formDetails) {
        const now = new Date();
        const pad = (n: number): string => String(n).padStart(2, '0');
        const timestamp = `${pad(now.getMonth() + 1)}${pad(now.getDate())}${now.getFullYear()}-${pad(now.getHours())}${pad(now.getMinutes())}`;
        const backupName = `${rightForm.formDetails.name}_bkup_${timestamp}`;
        addLog(`Creating backup as "${backupName}"...`);
        const { errors: backupErrors } = await cloneCustomForm(
          api,
          rightForm.formDetails,
          destWorkspace,
          backupName,
          addLog,
        );
        if (backupErrors.length) {
          setReplaceError(`Backup failed: ${backupErrors.join('; ')}`);
          return;
        }
        addLog('Backup created successfully.');
      }

      addLog('Replacing target custom form...');
      const { errors } = await copyCustomForm(
        api,
        leftForm.formDetails,
        matchedResults,
        { ...destWorkspace, formId: rightFormId },
        addLog,
      );

      if (errors.length) {
        setReplaceError(errors.join('; '));
      } else {
        setReplaceSuccess(true);
        addLog('Custom form replaced successfully.');
      }
    } catch (error) {
      const message = resolveActionError(error, 'Replace failed');
      setReplaceError(message);
      addLog(`ERROR: ${message}`);
    } finally {
      setIsReplacing(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-6 text-left">
      <h2 className="text-2xl font-light text-foreground">Replace Custom Form</h2>

      <div className="flex items-start gap-2 text-rose-700 dark:text-rose-300">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
        <span className="font-semibold">Warning</span>
      </div>
      <p className="text-sm text-foreground/70">
        This action will completely replace the target custom form. It cannot be undone. Consider
        taking a backup of the target form before proceeding.
      </p>

      <Alert className="border-amber-400/30 bg-amber-500/10 text-amber-100">
        <AlertDescription>
          <b>Note:</b> Conditional logic rules (show/hide, required-if) cannot be migrated
          automatically and must be recreated manually in the target environment. The Workfront API
          does not expose logic rule definitions.
        </AlertDescription>
      </Alert>

      {workspaceError && (
        <Alert className="border-rose-400/30 bg-rose-500/10 text-rose-100">
          <AlertDescription>{workspaceError}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          id="create-backup"
          checked={createBackup}
          onCheckedChange={(checked) => setCreateBackup(checked === true)}
          disabled={isDisabled}
        />
        <label
          htmlFor="create-backup"
          className="text-sm font-medium leading-none text-foreground/85"
        >
          Create backup form
        </label>
      </div>
      <p className="-mt-2 text-xs text-foreground/55">
        Original form will be backed up with <code>_bkup_MMDDYYYY-HHMM</code> name extension.
      </p>

      <div className="grid grid-cols-2 gap-6 max-lg:grid-cols-1">
        {/* Left panel (Source) */}
        <div className="rounded-lg border border-foreground/10 bg-foreground/[0.03] p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground/85">Source Instance</h3>
          <div className="flex flex-col gap-3">
            <Select
              value={leftWorkspaceRef}
              onValueChange={setLeftWorkspaceRef}
              disabled={isDisabled}
            >
              <SelectTrigger className="w-full border-foreground/15 bg-foreground/5 text-foreground">
                <SelectValue placeholder="Select source workspace…" />
              </SelectTrigger>
              <SelectContent>
                {sourceOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Combobox
              value={leftFormId ?? undefined}
              onValueChange={(value) => setLeftFormId(value || null)}
              disabled={!leftWorkspaceRef || leftForms.isLoading || isDisabled}
              placeholder={leftForms.isLoading ? 'Loading custom forms…' : 'Custom Form'}
              emptyText={leftForms.isLoading ? 'Loading custom forms…' : 'No custom forms found'}
              searchPlaceholder="Search custom forms…"
              options={leftForms.forms.map((form) => ({ value: form.ID, label: form.name }))}
            />
            {leftForms.error && (
              <div className="text-sm text-rose-800 dark:text-rose-200">
                Failed to load custom forms: {leftForms.error.message}
              </div>
            )}

            {leftForm.formDetails && (
              <div className="rounded border border-foreground/10 p-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-sm font-semibold text-foreground/85"
                  onClick={() => setShowLeftPreview((prev) => !prev)}
                >
                  <span>Preview</span>
                  {showLeftPreview ? (
                    <ChevronDown className="h-4 w-4 text-foreground/55" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-foreground/55" />
                  )}
                </button>
                {showLeftPreview && (
                  <div className="mt-2">
                    <CustomFormDetails
                      customLeftFormDetails={leftForm.formDetails}
                      customRightFormDetails={null}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right panel (Target — the form to overwrite) */}
        <div className="rounded-lg border border-foreground/10 bg-foreground/[0.03] p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground/85">Target Instance</h3>
          <div className="flex flex-col gap-3">
            <Select
              value={rightWorkspaceRef}
              onValueChange={setRightWorkspaceRef}
              disabled={isDisabled}
            >
              <SelectTrigger className="w-full border-foreground/15 bg-foreground/5 text-foreground">
                <SelectValue
                  placeholder={
                    targetOptions.length === 0
                      ? 'No other workspaces configured'
                      : 'Select target workspace…'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {targetOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Combobox
              value={rightFormId ?? undefined}
              onValueChange={(value) => setRightFormId(value || null)}
              disabled={!rightWorkspaceRef || rightForms.isLoading || isDisabled}
              placeholder={rightForms.isLoading ? 'Loading custom forms…' : 'Custom Form'}
              emptyText={rightForms.isLoading ? 'Loading custom forms…' : 'No custom forms found'}
              searchPlaceholder="Search custom forms…"
              options={rightForms.forms.map((form) => ({ value: form.ID, label: form.name }))}
            />
            {rightForms.error && (
              <div className="text-sm text-rose-800 dark:text-rose-200">
                Failed to load custom forms: {rightForms.error.message}
              </div>
            )}

            {rightForm.formDetails && (
              <div className="rounded border border-foreground/10 p-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-sm font-semibold text-foreground/85"
                  onClick={() => setShowRightPreview((prev) => !prev)}
                >
                  <span>Preview</span>
                  {showRightPreview ? (
                    <ChevronDown className="h-4 w-4 text-foreground/55" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-foreground/55" />
                  )}
                </button>
                {showRightPreview && (
                  <div className="mt-2">
                    <CustomFormDetails
                      customLeftFormDetails={null}
                      customRightFormDetails={rightForm.formDetails}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Changes panel */}
      {matchedResults && (
        <div className="rounded-lg border border-sky-400/30 bg-sky-500/5 p-4">
          <h3 className="mb-3 text-lg font-semibold text-foreground/85">Review Changes</h3>

          <div className="space-y-4">
            <div>
              <div className="mb-2 font-semibold text-sky-800 dark:text-sky-200">Groups:</div>
              <div className="space-y-1">
                {matchedResults.map(({ id, name, destName }) => (
                  <div
                    key={`group-${id}`}
                    className="grid grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] items-center gap-2"
                  >
                    <div className="min-w-0 break-words text-foreground/85">{name}</div>
                    <div className="flex justify-center">
                      <ArrowRight className="h-4 w-4 text-foreground/40" />
                    </div>
                    <div className="min-w-0 break-words text-foreground/85">
                      {destName || (
                        <span className="text-emerald-700 dark:text-emerald-300">new</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 font-semibold text-sky-800 dark:text-sky-200">Parameters:</div>
              <div className="space-y-1">
                {matchedResults.flatMap((group) =>
                  group.params.map(({ id, name, destId, destName, mismatch }) => (
                    <ParameterRow
                      key={`param-${group.id}-${id}`}
                      id={id}
                      name={name}
                      destId={destId}
                      destName={destName}
                      mismatch={mismatch}
                    />
                  )),
                )}
              </div>
            </div>

            {(() => {
              const paramsWithExpressions = matchedResults.flatMap((group) =>
                group.params.filter((p) => p.categoryParameterExpression?.customExpression),
              );
              if (!paramsWithExpressions.length) return null;
              return (
                <Alert className="border-amber-400/30 bg-amber-500/10 text-amber-100">
                  <AlertDescription>
                    <b>Note:</b> The following parameters have calculated expressions that cannot be
                    migrated automatically and must be recreated manually in the target environment:
                    <ul className="mt-1 list-inside list-disc">
                      {paramsWithExpressions.map((p) => (
                        <li key={p.id}>
                          <span className="font-medium">{p.name}</span>
                          <span className="ml-1 text-xs opacity-75">
                            ({p.categoryParameterExpression?.customExpression})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              );
            })()}
          </div>
        </div>
      )}

      {compareError && (
        <Alert className="border-rose-400/30 bg-rose-500/10 text-rose-100">
          <AlertDescription>{compareError}</AlertDescription>
        </Alert>
      )}
      {replaceError && (
        <Alert className="border-rose-400/30 bg-rose-500/10 text-rose-100">
          <AlertDescription>{replaceError}</AlertDescription>
        </Alert>
      )}
      {replaceSuccess && (
        <Alert className="border-emerald-400/30 bg-emerald-500/10 text-emerald-100">
          <AlertDescription>Custom form replaced successfully.</AlertDescription>
        </Alert>
      )}

      {logs.length > 0 && (
        <div className="max-h-[200px] overflow-auto rounded-md border border-foreground/10 bg-black/30 p-3 font-mono text-xs text-foreground/75">
          {logs.map((log, index) => (
            <div key={index} className="border-b border-foreground/5 py-0.5 last:border-b-0">
              {log}
            </div>
          ))}
        </div>
      )}

      <div ref={bottomRef} />

      <div className="sticky bottom-0 flex gap-3 border-t border-foreground/10 bg-black/40 py-4 backdrop-blur">
        {replaceSuccess || replaceError ? (
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        ) : (
          <>
            {matchedResults ? (
              <Button
                disabled={isDisabled || isReplacing}
                variant="destructive"
                onClick={() => setIsConfirmOpen(true)}
              >
                {isReplacing ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                    Replacing…
                  </>
                ) : (
                  'Replace'
                )}
              </Button>
            ) : (
              <Button
                disabled={
                  !leftForm.formDetails ||
                  !rightFormId ||
                  !rightWorkspaceRef ||
                  isDisabled ||
                  isComparing
                }
                onClick={() => void handleReviewChanges()}
              >
                {isComparing ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                    Comparing…
                  </>
                ) : (
                  'Review Changes'
                )}
              </Button>
            )}
            <Button variant="outline" onClick={onCancel} disabled={isReplacing}>
              Cancel
            </Button>
          </>
        )}
      </div>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm replace target form</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-rose-600 dark:text-rose-400">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <span className="font-semibold">Warning</span>
            </div>
            <p className="text-sm text-muted-foreground">
              You&apos;re about to replace the existing form with the source form&apos;s structure.
              {createBackup ? (
                <>
                  <br />A backup of the current target will be created automatically.
                </>
              ) : null}
            </p>
            <p className="text-sm text-muted-foreground">
              Once replaced, this action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setIsConfirmOpen(false);
                void handleReplace();
              }}
            >
              Yes, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
